/**
 * Chips sobre o mapa que substituem a antiga barra superior:
 * - `BrandChip` (topo-esquerda): no celular, a linha do topo (marca, relógio e
 *   "Pesquisar"); no desktop, marca + status, só quando a barra lateral está
 *   recolhida (aberta, a marca fica no cabeçalho dela).
 * - `MapActions`: no desktop, o campo da busca por município no meio do topo do
 *   mapa e, à direita, ao lado do zoom, Compartilhar, Exportar e Sobre. No
 *   celular, um único botão "⋯" abre as três ações numa folha, e a busca fica
 *   no topo, ao lado do relógio.
 * Fundo opaco (não `glass`): legível sobre nuvem branca e sobre a noite.
 */
import { RelogioDaPrevisao } from '../previsao/RelogioDaPrevisao';
import { usePrevisaoStore } from '../previsao/store';
import clsx from 'clsx';
import { Clapperboard, Ellipsis, Expand, Info, Share2, Shrink } from 'lucide-react';
import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { flyHome, SIDEBAR_WIDTH } from '../map/viewControl';
import { useMapInstance } from '../map/mapContext';
import { useUiStore } from '../state/ui';
import { usePainelLateralAberto } from './hooks';
import { useViewStore } from '../state/view';
import { BrandMark } from './Brand';
import { LatestStatus } from './LatestStatus';
import { Button, Dialog, IconButton } from './primitives';
import { shareCurrentView } from './share';
import { BuscaMunicipio } from '../busca/BuscaMunicipio';

/** Estilo comum dos chips sobre o mapa: opaco, borda fina, raio 12. */
export const CHIP = 'border border-ink-600/90 bg-ink-900/95 text-ink-100 shadow-lg';

export function BrandChip(props: { mobile: boolean }) {
  if (props.mobile) {
    // Celular (29/10/2026, pedido do usuário): o relógio desceu para o pé do mapa (ui/RelogioDoPe; no
    // "Próximos dias", o da previsão, acima da escala), e o topo ficou igual nos dois modos: a marca à
    // esquerda (abre o "Sobre"; 44 px, como a casa na coluna da direita) e "Pesquisar" no meio. Em pé a
    // busca vai de 8 px depois da marca a 8 px antes da coluna (até 384 px, no meio); deitado, 224 px no
    // meio do mapa, e a marca à direita do trilho (`--trilho-fim`).
    return (
      <>
        <button
          type="button"
          onClick={() => useUiStore.getState().set({ aboutOpen: true })}
          aria-label="AmaView: sobre o portal"
          title="AmaView: sobre o portal"
          data-chip-do-topo=""
          className={clsx(
            CHIP,
            'absolute left-[calc(8px+env(safe-area-inset-left))] z-20 flex size-11 items-center justify-center rounded-xl hover:bg-ink-800 focus-visible:outline-2 focus-visible:outline-mata-400',
            'deitado:left-[var(--trilho-fim)]',
          )}
          style={{ top: 'calc(env(safe-area-inset-top) + 12px)' }}
        >
          <BrandMark className="size-7" />
        </button>
        <div
          className={clsx(
            'absolute right-[calc(60px+env(safe-area-inset-right))] left-[calc(60px+env(safe-area-inset-left))] z-20 mx-auto max-w-sm',
            'deitado:right-auto deitado:left-1/2 deitado:mx-0 deitado:w-56 deitado:-translate-x-1/2',
          )}
          style={{ top: 'calc(env(safe-area-inset-top) + 12px)' }}
          data-busca-do-topo=""
        >
          <BuscaMunicipio variante="celular" className="w-full" />
        </div>
      </>
    );
  }
  return (
    <div className={clsx(CHIP, 'flex h-9 items-center gap-2 rounded-xl pr-2.5 pl-1.5')}>
      <BrandMark className="size-6 shrink-0" />
      <span className="text-sm font-semibold tracking-tight text-white">AmaView</span>
      <LatestStatus variant="inline" className="min-w-0" />
    </div>
  );
}

/** Só o status das imagens (desktop com o painel recolhido; a marca está no trilho). */
export function StatusChip() {
  const modoPrevisao = usePrevisaoStore((s) => s.modo);
  return (
    <div className={`${CHIP} absolute top-3 left-3 z-20 flex ${modoPrevisao ? 'h-11' : 'h-9'} items-center rounded-xl px-2.5`} data-status-chip="">
      {modoPrevisao ? <RelogioDaPrevisao /> : <LatestStatus variant="inline" className="min-w-0" />}
    </div>
  );
}

function MoreMenu(props: { open: boolean; onClose: () => void }) {
  const set = useUiStore((s) => s.set);
  const fillMode = useViewStore((s) => s.fillMode);
  const map = useMapInstance((s) => s.map);
  const run = (fn: () => void) => () => {
    props.onClose();
    fn();
  };
  const toggleFill = () => {
    useViewStore.setState({ fillMode: fillMode === 'preencher' ? 'setor' : 'preencher' });
    if (map) flyHome(map);
  };
  return (
    <Dialog open={props.open} onClose={props.onClose} title="Mais">
      <div className="flex flex-col gap-2">
        <Button
          variant="secondary"
          icon={fillMode === 'preencher' ? Shrink : Expand}
          onClick={run(toggleFill)}
          className="min-h-11 justify-start"
          title="Em pé, a imagem do satélite é mais larga que a tela: preencher corta um pouco dos lados; o setor inteiro deixa faixas pretas."
        >
          {fillMode === 'preencher' ? 'Ver o setor inteiro (com faixas)' : 'Preencher a tela com a imagem'}
        </Button>
        <Button variant="secondary" icon={Share2} onClick={run(() => void shareCurrentView())} className="min-h-11 justify-start">
          Compartilhar link desta visualização
        </Button>
        <Button variant="secondary" icon={Clapperboard} onClick={run(() => set({ exportOpen: true }))} className="min-h-11 justify-start">
          Exportar animação (GIF/MP4)
        </Button>
        <Button variant="secondary" icon={Info} onClick={run(() => set({ aboutOpen: true }))} className="min-h-11 justify-start">
          Sobre o AmaView
        </Button>
      </div>
    </Dialog>
  );
}

/** Largura do campo da busca no topo do desktop (px): a ideal e a menor antes de ele voltar para as ações. */
const BUSCA_IDEAL_PX = 400;
const BUSCA_MINIMA_PX = 240;
/** Da borda direita do mapa até as ações (a coluna do zoom, `right-14`). */
const ATE_AS_ACOES_PX = 56;
const FOLGA_PX = 12;

/**
 * Onde fica o campo da busca no desktop (pedido do usuário, 09/10/2026: "bem centralizado no topo"):
 * no meio do mapa, com até 400 px. Se encostar no painel lateral (ou no chip do status) à esquerda ou
 * nas ações à direita, desliza para o lado livre e encolhe; abaixo de 240 px livres, volta a ser o
 * primeiro das ações (null). Medido no DOM, como as fichas do mapa.
 */
function usePosicaoDaBusca(nav: RefObject<HTMLElement | null>, sidebarOpen: boolean): { left: number; width: number } | null {
  const [pos, setPos] = useState<{ left: number; width: number } | null>({ left: 0, width: BUSCA_IDEAL_PX });
  useLayoutEffect(() => {
    const el = nav.current;
    const main = el?.parentElement;
    if (!el || !main) return;
    const medir = () => {
      const m = main.getBoundingClientRect();
      // As ações sem o campo (quando ele está nelas, o vão de 8 px dele também sai).
      const naNav = el.querySelector<HTMLElement>('[data-busca-nas-acoes]');
      const acoes = el.offsetWidth - (naNav ? naNav.offsetWidth + 8 : 0);
      const chip = main.querySelector<HTMLElement>('[data-status-chip]')?.getBoundingClientRect();
      const esquerda = sidebarOpen ? SIDEBAR_WIDTH + FOLGA_PX : chip ? chip.right - m.left + FOLGA_PX : FOLGA_PX;
      const direita = m.width - ATE_AS_ACOES_PX - acoes - FOLGA_PX;
      const livre = direita - esquerda;
      if (livre < BUSCA_MINIMA_PX) {
        setPos((p) => (p === null ? p : null));
        return;
      }
      const width = Math.min(BUSCA_IDEAL_PX, livre);
      const left = Math.round(Math.min(Math.max(m.width / 2 - width / 2, esquerda), direita - width));
      setPos((p) => (p && p.left === left && p.width === width ? p : { left, width }));
    };
    medir();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    ro?.observe(main);
    ro?.observe(el);
    const chip = main.querySelector<HTMLElement>('[data-status-chip]');
    if (chip) ro?.observe(chip);
    return () => ro?.disconnect();
  }, [nav, sidebarOpen]);
  return pos;
}

export function MapActions(props: { mobile: boolean }) {
  const set = useUiStore((s) => s.set);
  const moreOpen = useUiStore((s) => s.moreOpen);
  // O das observadas ou o da previsão.
  const sidebarOpen = usePainelLateralAberto();
  const openExport = () => set({ exportOpen: true });
  const openAbout = () => set({ aboutOpen: true });
  const navRef = useRef<HTMLElement>(null);
  const busca = usePosicaoDaBusca(navRef, sidebarOpen && !props.mobile);

  if (props.mobile) {
    return (
      <>
        {/* Parte da coluna que a ficha do mapa não cobre (map/popupArea). */}
        <div className="absolute right-[calc(8px+env(safe-area-inset-right))] z-20" style={{ top: 'calc(env(safe-area-inset-top) + 12px + 44px + 8px)' }} data-coluna-do-mapa="">
          <IconButton
            icon={Ellipsis}
            size="lg"
            label="Mais: compartilhar, exportar, sobre"
            onClick={() => set({ moreOpen: true })}
            className={`${CHIP} rounded-xl`}
          />
        </div>
        <MoreMenu open={moreOpen} onClose={() => set({ moreOpen: false })} />
      </>
    );
  }

  // A linha das ações para antes do painel lateral (aberto) ou do chip do status (recolhido). As caixas
  // têm só a largura do conteúdo: as fichas do mapa desviam delas (map/PopupHost).
  const inicio = sidebarOpen ? SIDEBAR_WIDTH + FOLGA_PX : 200;
  return (
    <>
      {/* A busca por município, no meio do topo do mapa (src/busca); sem lugar, a primeira das ações. */}
      {busca && (
        <div className="absolute top-3 z-20" style={{ left: busca.left, width: busca.width }} data-busca-no-topo="">
          <BuscaMunicipio variante="desktop" className="w-full" />
        </div>
      )}
      <nav
        ref={navRef}
        className="absolute top-3 right-14 z-20 flex items-center gap-2"
        style={{ maxWidth: `calc(100% - ${inicio}px - ${ATE_AS_ACOES_PX}px)` }}
        aria-label="Ações"
      >
        {!busca && (
          <div className="w-80 min-w-40 shrink" data-busca-nas-acoes="">
            <BuscaMunicipio variante="desktop" className="w-full" />
          </div>
        )}
        {/* Abaixo de 1280 px, Compartilhar e Exportar ficam só com o ícone. */}
        <Button variant="secondary" size="sm" icon={Share2} onClick={() => void shareCurrentView()} title="Copiar o link desta visualização" className={`${CHIP} h-9 rounded-xl max-xl:w-9 max-xl:px-0`}>
          <span className="sr-only xl:not-sr-only">Compartilhar</span>
        </Button>
        <Button variant="secondary" size="sm" icon={Clapperboard} onClick={openExport} title="Salvar a animação como GIF ou vídeo" className={`${CHIP} h-9 rounded-xl max-xl:w-9 max-xl:px-0`}>
          <span className="sr-only xl:not-sr-only">Exportar</span>
        </Button>
        <IconButton icon={Info} label="Sobre o AmaView" onClick={openAbout} className={`${CHIP} rounded-xl`} />
      </nav>
    </>
  );
}
